import mixpanel from "mixpanel-browser";

// Configurable blocklist via env var (comma-separated), defaults to empty (track everyone)
const INTERNAL_DOMAINS: string[] = (import.meta.env.VITE_ANALYTICS_INTERNAL_DOMAINS || "")
  .split(",")
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

let initialized = false;
let isInternal = false;
let isImpersonating = false;

export function isAnalyticsEnabled(): boolean {
  return import.meta.env.VITE_ANALYTICS_ENABLED === "true";
}

export function isInternalUser(email: string | undefined): boolean {
  if (!email) return false;
  const domain = email.split("@")[1]?.toLowerCase();
  return INTERNAL_DOMAINS.some((d) => domain === d);
}

export function setCurrentUserInternal(internal: boolean): void {
  isInternal = internal;
}

export function setCurrentUserImpersonating(impersonating: boolean): void {
  isImpersonating = impersonating;
}

export function shouldTrack(): boolean {
  return isAnalyticsEnabled() && !isInternal && !isImpersonating;
}

export function initMixpanel(): boolean {
  if (!isAnalyticsEnabled()) {
    return false;
  }
  if (initialized) return initialized;

  const token = import.meta.env.VITE_MIXPANEL_TOKEN;
  if (!token) {
    return false;
  }

  mixpanel.init(token, {
    debug: import.meta.env.DEV,
    // Feature flags are NOT fetched by the browser SDK: RAP's backend evaluates them
    // (GET /feature-flags, RAP-668) so an ad blocker can't switch the portal off. See
    // src/services/analytics/featureFlags.ts.
    flags: false,
    persistence: "localStorage",
    record_block_class: "mp-block",
    record_mask_text_class: "mp-mask",
    record_mask_text_selector: "[data-plaintiff]",
    record_sessions_percent: 0,
    track_pageview: false,
  });

  initialized = true;
  return true;
}

export function getMixpanel() {
  return shouldTrack() && initialized ? mixpanel : null;
}

/**
 * Identify the user in Mixpanel regardless of shouldTrack(), so the browser
 * profile uses the same distinct_id (the user id) the server evaluates flags
 * for. This only creates/links a profile and sends no events.
 */
export function identifyForFlags(userId: string): void {
  if (initialized) mixpanel.identify(userId);
}

/** Clear the identified profile + viewer-state flags on logout. Raw reset so it
 * also covers internal users we identify-for-flags but otherwise don't track. */
export function resetIdentity(): void {
  if (initialized) mixpanel.reset();
  isInternal = false;
  isImpersonating = false;
}

/**
 * Opt-in: recording runs only where a stage sets `VITE_MIXPANEL_SESSION_RECORDING=true` (prod).
 * It used to be on unless the value was exactly `"false"`, so a stage that omitted the key — or
 * spelled it `False` — would start recording sessions without anyone deciding it should.
 */
export function isSessionRecordingEnabled(): boolean {
  return import.meta.env.VITE_MIXPANEL_SESSION_RECORDING?.trim().toLowerCase() === "true";
}

export function startSessionRecording(): void {
  if (!isSessionRecordingEnabled()) return;
  getMixpanel()?.start_session_recording();
}

export function stopSessionRecording(): void {
  getMixpanel()?.stop_session_recording();
}
